import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { line, HISTORY_WINDOW_ROWS, Size, formatDateOnly, t, type PagedRecord } from '@bms2/shared';
import { rememberTileFolded, tileFolded, useRowCap } from '../../components/composites/Tile';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { Pill } from '../../components/controls';
import { Icon } from '../../components/Icon';
import { useDateFormat } from '../../shell/ViewerZone';
import { type TileRow } from './tileItems';
import { whenText } from '../../shell/viewerDates';
import styles from './DocumentPage.module.css';

/**
 * 🔒 What has happened to a document, on the document's own page (decision
 * 0470): the merged reverse-chronological list the record page's History tile
 * draws — notes, past events, closed checklist items and audit rows — over the
 * same rows.
 *
 * 🔨 An audit trail a page stops drawing is a capability lost, not a layout
 * changed. It is read-only here, as it is on a record page: nothing in it is an
 * action and no row is edited.
 */
/**
 * The name the History fold is remembered under: the record page's History
 * tile's own title, so one fold serves both (decision 0500). 🔨 It is the tile's
 * name, never a translated heading, so a language change keeps the choice.
 */
export const HISTORY_FOLD_NAME = 'History';

export interface DocumentHistoryPanelProps {
  /** 🔨 The kind the rows belong to, which is also the visit's own audit when a Service Report names it (T2396). */
  recordType: PagedRecord | 'WorkOrderVisit';
  recordId: string;
  /** The history rows the page answer carries, newest first, in the shape the History tile draws. */
  rows: readonly TileRow[];
  /** The viewer's zone, so an instant reads in their own day and never the stored clock (decision 0261). */
  timeZone?: string;
  /**
   * 🔒 Whether the panel folds by its own chevron. A rail whose one chevron folds it, the Work Order's (decision
   * 6683), passes false: the panel stands open, reads no remembered fold and draws no chevron, so a History folded
   * on another page never opens there as a bare heading with nothing to open it (review of batch UX3).
   */
  foldable?: boolean;
}

export function DocumentHistoryPanel({ recordType, recordId, rows, timeZone = 'UTC', foldable = true }: DocumentHistoryPanelProps) {
  const dateFormat = useDateFormat();
  const title = t('documentHistory.title');
  /*
   * 🔒 The panel folds by the History tile's own remembered fold (T588; decision
   * 1404, on 0500): one choice per device under the one name, so History folded
   * on a Company opens folded on an Invoice too.
   */
  const [remembered, setFolded] = useState(() => (foldable ? tileFolded(HISTORY_FOLD_NAME) : false));
  const folded = foldable && remembered;
  const fold = (next: boolean) => {
    setFolded(next);
    rememberTileFolded(HISTORY_FOLD_NAME, next);
  };
  // 🔒 Five rows stand and the rest scroll inside the list, the record page's History window (T588; decision 1404).
  /* 🔨 The window is the list's own wrapper, so the shared list scrolls inside it and the rows are its list's children (T3227). */
  const ref = useRef<HTMLDivElement>(null);
  const cap = useRowCap(ref, folded ? 0 : rows.length, HISTORY_WINDOW_ROWS, (el) => Array.from(el.firstElementChild?.children ?? []));
  return (
    <section className={styles.block} data-document-history={recordId} data-document-history-for={recordType} data-document-history-count={rows.length} data-document-history-fold={folded ? 'folded' : 'open'}>
      <div className={styles.foldHead}>
        <h2 className={styles.heading}>{title}</h2>
        {/* 🔨 Folded leaves the heading and this chevron standing (decision 0500). */}
        {foldable ? <button
          type="button"
          className={styles.foldToggle}
          onClick={() => fold(!folded)}
          aria-expanded={!folded}
          aria-label={t(folded ? 'tile.expand' : 'tile.collapse')}
          title={t(folded ? 'tile.expand' : 'tile.collapse')}
          data-tile-fold={folded ? 'folded' : 'open'}
        >
          <Icon meaning={folded ? 'expand' : 'collapse'} size="inline" />
        </button> : null}
      </div>
      {folded ? null : rows.length === 0 ? <p className={styles.muted}>{t('documentHistory.none')}</p> : null}
      {folded ? null : (
      <div
        ref={ref}
        className={styles.historyWindow}
        data-history-window={HISTORY_WINDOW_ROWS}
        data-history-capped={cap === null ? 'false' : 'true'}
        style={cap === null ? undefined : { maxHeight: cap }}
      >
        <DetailList look="separator">
          {rows.map((row, index) => (
            <DetailRow
              key={row.id ?? `${row.label}-${index}`}
              data-document-history-row={row.id ?? row.label}
              title={row.route ? <Link to={row.route}>{row.label}</Link> : row.label}
              detail={line(row.value, row.at || row.day ? row.detail : null)}
              columns={[{ value: row.at ? whenText(row.at, timeZone, dateFormat) : row.day ? formatDateOnly(row.day, dateFormat) : (row.detail ?? '') }]}
              badges={row.pill ? (
                <Pill text={row.pill} foreground={`var(--status-${row.pillGroup ?? 'inactive'}-text)`} background={`var(--status-${row.pillGroup ?? 'inactive'}-bg)`} size={Size.Small} />
              ) : null}
            />
          ))}
        </DetailList>
      </div>
      )}
    </section>
  );
}
